// §4.7 Recall Trace Viewer (office) — the one-query recall answer.
//
// Root node card · direction toggle (↑ upstream / ↓ downstream) · the indented
// DAG with depth glyphs, relation labels and consumed quantities · the summary
// tiles · "Export recall pack" (CSV) · the truncation banner.
//
// THE HONEST PART, and the reason this screen exists the way it does: on a
// downstream trace the response carries `customer_resolution`, and it is a
// claim about COMPLETENESS. `unavailable` (no sales surface deployed) renders
// as "waiting on Module 09". `partial` (D-286 ruling 24 — module 09 is bound,
// but some shipment on the trace has no sales shipment document behind it:
// seeded stock, stock shipped before module 09 existed) renders the customers
// that DID resolve under a sentence saying how many shipments did not, and the
// tile never prints a bare number. Only `resolved` prints a count a reviewer
// may act on. On a recall, "0 customers affected" and "we cannot tell you
// every customer affected" are opposite facts, and only one of them is true.
// The shipment rows are this module's OWN ledger and are always real.
//
// Cycle safety: the server caps depth/nodes and flags `truncated` + `boundary`;
// the client walk additionally carries a path set so corrupt data cannot spin
// the renderer (§9.13 — corrupt data must never read as clean).

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable } from "../../../../components/data-table";
import { SegmentedControl } from "../../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { WaitingOnModule } from "../components/inventory-ui";
import {
  useLot,
  useLotTrace,
  type TraceDirection,
  type TraceEdge,
  type TraceNode,
  type TraceResponse,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { csvConventionFor, downloadCsv, toCsv } from "../../../../lib/csv";
import { formatTimestamp, shortId, formatMeasureWithUom } from "../lib/measure";

interface WalkRow {
  readonly key: string;
  readonly node: TraceNode | undefined;
  readonly nodeId: string;
  readonly kind: "lot" | "stock_unit";
  readonly depth: number;
  readonly relation?: string;
  readonly quantity?: string;
  readonly uom?: string;
  readonly repeated: boolean;
}

const MAX_ROWS = 2000;

/** How far the tree indents before it stops indenting and starts numbering. */
const MAX_INDENT_STEPS = 8;

export interface WalkResult {
  readonly rows: WalkRow[];
  /**
   * True when the CLIENT walk stopped at MAX_ROWS. Distinct from the server's
   * `truncated`: a graph the client cut used to render as a complete recall
   * answer, which is the §9.13 failure ("corrupt/partial data must never read
   * as clean") on the screen where it matters most.
   */
  readonly truncated: boolean;
}

/** Depth-first walk of the DAG from the root, cycle-guarded by the path set. */
export function walkTrace(response: TraceResponse, rootId: string): WalkResult {
  const byId = new Map(response.nodes.map((node) => [node.id, node]));
  const children = new Map<string, TraceEdge[]>();
  for (const edge of response.edges) {
    const from = response.direction === "down" ? edge.parent_id : edge.child_id;
    const list = children.get(from);
    if (list === undefined) children.set(from, [edge]);
    else list.push(edge);
  }

  const rows: WalkRow[] = [];
  const seen = new Set<string>();
  let truncated = false;

  function visit(
    nodeId: string,
    kind: "lot" | "stock_unit",
    depth: number,
    path: Set<string>,
    edge?: TraceEdge,
  ) {
    if (rows.length >= MAX_ROWS) {
      truncated = true;
      return;
    }
    const repeated = seen.has(nodeId);
    seen.add(nodeId);
    rows.push({
      key: `${path.size}-${nodeId}-${String(rows.length)}`,
      node: byId.get(nodeId),
      nodeId,
      kind,
      depth,
      repeated,
      ...(edge !== undefined
        ? { relation: edge.relation, quantity: edge.quantity, uom: edge.uom }
        : {}),
    });
    // A node already expanded elsewhere is listed but not re-expanded — the
    // DAG stays readable and a diamond cannot explode into a combinatorial tree.
    if (repeated || path.has(nodeId)) return;
    const nextPath = new Set(path);
    nextPath.add(nodeId);
    for (const child of children.get(nodeId) ?? []) {
      const targetId = response.direction === "down" ? child.child_id : child.parent_id;
      const targetKind = response.direction === "down" ? child.child_kind : child.parent_kind;
      visit(targetId, targetKind, depth + 1, nextPath, child);
    }
  }

  visit(rootId, "lot", 0, new Set());
  return { rows, truncated };
}

export function TraceViewerScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();
  const params = useParams({ strict: false });
  const id = typeof params.id === "string" ? params.id : "";

  const [direction, setDirection] = useState<TraceDirection>(() =>
    window.location.hash === "#up" ? "up" : "down",
  );

  // A read-only surface does not get refused by the MIRROR: a custom tenant
  // role resolves to zero client-side grants by design and would be told "you
  // do not have permission" for a read the server would allow, and a FAILED
  // role feed would turn an outage into a false authorization statement. The
  // request goes; a real 403 is rendered from the server's own problem copy.
  const canTrace = perms.has("inventory.trace.read");
  const mirrorRefuses = !canTrace && perms.isResolved && !perms.isFeedError;
  const lot = useLot(id);
  const trace = useLotTrace(id, direction, !mirrorRefuses);

  useEffect(() => {
    if (trace.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [trace.error, navigate]);

  const walk = useMemo(
    () =>
      trace.data === undefined
        ? { rows: [] as WalkRow[], truncated: false }
        : walkTrace(trace.data, id),
    [trace.data, id],
  );
  const rows = walk.rows;

  if (mirrorRefuses) {
    return (
      <EmptyState
        title={t("inventory.trace.noPermissionTitle")}
        body={t("inventory.trace.noPermissionBody")}
      />
    );
  }

  if (trace.isPending || trace.error instanceof UnauthorizedError) {
    return <CardSkeleton lines={8} label={t("inventory.trace.loading")} />;
  }

  if (trace.error) {
    return (
      <ErrorState
        title={t("inventory.trace.errorTitle")}
        detail={trace.error.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => void trace.refetch()}
        requestId={requestIdOf(trace.error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }

  const data = trace.data;
  const summary = data.summary;
  const customersUnavailable = data.customer_resolution === "unavailable";
  // `partial`: a customer list that is TRUE and INCOMPLETE. The count of
  // shipments nobody could be named for travels with it everywhere the list
  // does — the tile, the note and the exported pack.
  const customersPartial = data.customer_resolution === "partial";
  const unresolvedShipments = summary.unresolved_shipments ?? 0;

  function exportPack() {
    const header = [
      "record_type",
      "id",
      "reference",
      "quantity",
      "uom",
      "moved_at",
      "note",
    ] as const;
    const body: (string | number | null | undefined)[][] = [];
    for (const shipment of data.shipments ?? []) {
      body.push([
        "shipment",
        shipment.movement_id,
        shipment.source_doc_id ?? shipment.lot_id ?? shipment.stock_unit_id,
        shipment.qty_base,
        shipment.uom,
        shipment.moved_at,
        "",
      ]);
    }
    for (const customer of data.customers ?? []) {
      body.push(["customer", customer.customer_id, customer.customer_name, "", "", "", ""]);
    }
    if (customersUnavailable) {
      // The pack is a document someone acts on OFFLINE — the degradation has to
      // travel with it, not stay behind on the screen.
      body.push(["note", "", "", "", "", "", t("inventory.trace.customersUnavailablePlain")]);
    }
    if (customersPartial) {
      body.push([
        "note",
        "",
        "",
        "",
        "",
        "",
        t("inventory.trace.customersPartialPlain", {
          unresolved: unresolvedShipments,
          total: summary.shipments ?? 0,
        }),
      ]);
    }
    if (data.truncated) {
      body.push(["note", "", "", "", "", "", t("inventory.trace.truncatedPlain")]);
    }
    if (walk.truncated) {
      body.push(["note", "", "", "", "", "", t("inventory.trace.clientTruncatedPlain")]);
    }
    downloadCsv(
      `recall-pack-${lot.data?.lot.lot_code ?? shortId(id)}-${direction}.csv`,
      toCsv(header, body, {
        convention: csvConventionFor(i18n.language),
        decimalHeaders: ["quantity"],
      }),
    );
  }

  return (
    <section className="trace-viewer" data-testid="trace-screen">
      <PageHeader
        crumbs={[
          {
            label: `${term("lot")} ${lot.data?.lot.lot_code ?? shortId(id)}`,
            to: `/inventory/lots/${id}`,
          },
        ]}
        title={t("inventory.trace.title")}
        actions={
          <>
            <Button data-testid="trace-export" onClick={exportPack}>
              {t("inventory.trace.exportPack")}
            </Button>
          </>
        }
      />

      <div className="trace-root ds-card">
        <h2>
          {term("lot")} {lot.data?.lot.lot_code ?? shortId(id)}
        </h2>
        <SegmentedControl
          options={[
            { value: "up", label: t("inventory.trace.up") },
            { value: "down", label: t("inventory.trace.down") },
          ]}
          value={direction}
          onChange={setDirection}
          label={t("inventory.trace.direction")}
        />
      </div>

      {data.truncated ? (
        <p className="degraded-note is-warning" role="note">
          {t("inventory.trace.truncated")}
        </p>
      ) : null}
      {walk.truncated ? (
        <p className="degraded-note is-warning" role="note">
          {t("inventory.trace.clientTruncated", { n: MAX_ROWS })}
        </p>
      ) : null}

      <div className="summary-tiles" data-testid="trace-tiles">
        {direction === "down" ? (
          <>
            <Tile
              tile="finished_lots"
              label={t("inventory.trace.tileFinishedLots")}
              value={summary.finished_lots}
            />
            <Tile
              tile="stock_units"
              label={t("inventory.trace.tileStockUnits")}
              value={summary.stock_units}
            />
            <Tile
              tile="shipments"
              label={t("inventory.trace.tileShipments")}
              value={summary.shipments}
            />
            <Tile
              tile="customers"
              label={t("inventory.trace.tileCustomers")}
              value={customersUnavailable ? undefined : summary.customers}
              unavailableOwner={customersUnavailable ? "09" : undefined}
              incomplete={customersPartial}
              incompleteLabel={t("inventory.trace.tileIncomplete")}
            />
          </>
        ) : (
          <>
            <Tile
              tile="raw_lots"
              label={t("inventory.trace.tileRawLots")}
              value={summary.raw_lots}
            />
            <Tile
              tile="suppliers"
              label={t("inventory.trace.tileSuppliers")}
              value={summary.suppliers}
            />
          </>
        )}
      </div>

      {customersUnavailable ? (
        <p className="degraded-note" data-testid="trace-customers-unavailable" role="note">
          {t("inventory.trace.customersUnavailable")}
        </p>
      ) : null}
      {customersPartial ? (
        <p
          className="degraded-note"
          data-testid="trace-customers-partial"
          data-unresolved={unresolvedShipments}
          role="note"
        >
          {t("inventory.trace.customersPartial", {
            unresolved: unresolvedShipments,
            total: summary.shipments ?? 0,
          })}
        </p>
      ) : null}

      <h2>{t("inventory.trace.graph")}</h2>
      {rows.length <= 1 ? (
        <EmptyState
          title={t("inventory.trace.emptyTitle")}
          body={
            direction === "down"
              ? t("inventory.trace.emptyDownBody")
              : t("inventory.trace.emptyUpBody")
          }
        />
      ) : (
        // The server caps depth at 50; 50 × 1.25rem is ~1000px of padding and
        // the tree is not a table, so an unbounded indent pushed the page body
        // sideways. The indent stops at MAX_INDENT_STEPS and the depth is
        // printed instead, and the tree scrolls inside its own box.
        <div className="trace-scroll">
          <ul className="trace-tree" data-testid="trace-tree">
            {rows.map((row) => (
              <li
                key={row.key}
                className="trace-row"
                data-testid="trace-row"
                data-kind={row.kind}
                data-node={row.nodeId}
                style={{
                  paddingLeft: `${String(Math.min(row.depth, MAX_INDENT_STEPS) * 1.25)}rem`,
                }}
              >
                <span className="trace-glyph" aria-hidden="true">
                  {row.depth === 0 ? "●" : "└─"}
                </span>
                {row.depth > MAX_INDENT_STEPS ? (
                  <span className="trace-depth">
                    {t("inventory.trace.depth", { n: row.depth })}
                  </span>
                ) : null}
                {row.kind === "lot" ? (
                  <Link to="/inventory/lots/$id" params={{ id: row.nodeId }}>
                    {row.node?.label ?? shortId(row.nodeId)}
                  </Link>
                ) : (
                  <Link to="/inventory/units/$id" params={{ id: row.nodeId }}>
                    {row.node?.label ?? shortId(row.nodeId)}
                  </Link>
                )}
                {row.relation !== undefined ? (
                  <span className="rel-label">
                    {enumLabel(t, "inventory.vocab.relation", row.relation)}
                  </span>
                ) : null}
                {row.quantity !== undefined ? (
                  <span className="rel-qty">
                    {formatMeasureWithUom(row.quantity, row.uom, { locale: i18n.language })}
                  </span>
                ) : null}
                {row.node?.qc_status != null ? (
                  <span className="rel-qc">
                    {enumLabel(t, "inventory.vocab.qc", row.node.qc_status)}
                  </span>
                ) : null}
                {row.repeated ? (
                  <span className="trace-repeat">{t("inventory.trace.alreadyShown")}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(data.boundary ?? []).length > 0 ? (
        <>
          <h2>{t("inventory.trace.boundary")}</h2>
          <p className="inv-hint">{t("inventory.trace.boundaryHint")}</p>
          <ul className="inv-list">
            {(data.boundary ?? []).map((node) => (
              <li key={node.id}>{node.label}</li>
            ))}
          </ul>
        </>
      ) : null}

      {direction === "down" ? (
        <>
          <h2>{t("inventory.trace.shipments")}</h2>
          {(data.shipments ?? []).length === 0 ? (
            <p className="inv-hint">{t("inventory.trace.noShipments")}</p>
          ) : (
            <DataTable
              columns={[
                {
                  key: "moved_at",
                  header: t("inventory.trace.colWhen"),
                  id: true,
                  render: (shipment) => formatTimestamp(shipment.moved_at, i18n.language),
                },
                {
                  key: "qty_base",
                  header: t("inventory.trace.colQty"),
                  numeric: true,
                  render: (shipment) =>
                    formatMeasureWithUom(shipment.qty_base, shipment.uom, {
                      locale: i18n.language,
                    }),
                },
                {
                  key: "source_doc_id",
                  header: t("inventory.trace.colRef"),
                  render: (shipment) =>
                    shipment.source_doc_id === null
                      ? shortId(shipment.movement_id)
                      : shortId(shipment.source_doc_id),
                },
              ]}
              rows={data.shipments ?? []}
              rowKey={(shipment) => shipment.movement_id}
              empty={null}
              rowTestId="trace-shipment"
            />
          )}
        </>
      ) : null}
    </section>
  );
}

function Tile({
  tile,
  label,
  value,
  unavailableOwner,
  incomplete = false,
  incompleteLabel,
}: {
  tile: string;
  label: string;
  value: number | undefined;
  unavailableOwner?: string | undefined;
  /** The number is TRUE but not the whole answer (`customer_resolution:
   * partial`). A bare count would be read as the total, so the tile prints
   * "N+" — or a dash when nothing resolved at all, because "0+" still starts
   * with the digit a hurried reviewer reads — under a word that says so. */
  incomplete?: boolean;
  incompleteLabel?: string | undefined;
}) {
  const known = value ?? 0;
  return (
    <div
      className="summary-tile"
      data-testid="trace-tile"
      data-tile={tile}
      data-value={unavailableOwner !== undefined ? "" : String(known)}
      data-unavailable={String(unavailableOwner !== undefined)}
      data-partial={String(incomplete)}
    >
      <span className="tile-label">{label}</span>
      <span className="tile-value">
        {unavailableOwner !== undefined ? (
          <WaitingOnModule ownerCode={unavailableOwner} />
        ) : incomplete ? (
          known > 0 ? (
            `${String(known)}+`
          ) : (
            "—"
          )
        ) : (
          known
        )}
      </span>
      {incomplete && incompleteLabel !== undefined ? (
        <span className="tile-label" data-testid="trace-tile-incomplete">
          {incompleteLabel}
        </span>
      ) : null}
    </div>
  );
}
